import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { alertsApi } from '@verlo/shared/services/api';
import { lowWords, newLow, newOrders, orderWords } from '../../lib/alerts';

// What the back office says as it happens (Oct 2026): stock running low, orders coming in.
// Asks the feed every 30 seconds while the tab is in front, from the time it last asked, so
// opening the back office doesn't replay the day. A new order also chimes, once the browser
// lets the page make a sound. Company › Alerts and My settings › Alerts for me switch it off;
// the feed answers with what's on, and says nothing for what's off.

const EVERY = 30000;

function chime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.12]].forEach(([f, at]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.3);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 0.32);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* no sound is fine */ }
}

export default function BackOfficeAlerts() {
  const navigate = useNavigate();
  const since = useRef(null);
  const said = useRef(new Set());        // low items said this session
  const saidOrders = useRef(new Set());  // orders said, or already there when the page opened

  useEffect(() => {
    let stopped = false;
    const ask = async () => {
      if (document.hidden) return;
      try {
        const { data } = await alertsApi.feed(since.current || undefined);
        if (stopped) return;
        const first = since.current === null;
        since.current = data.now;
        if (first) { (data.seen_orders || []).forEach((id) => saidOrders.current.add(id)); return; }
        const low = newLow(data.low_stock, said.current);
        low.forEach((i) => said.current.add(i.id));
        const lw = lowWords(low, data.low_more);
        if (lw) toast.warning(lw, { action: { label: 'Order', onClick: () => navigate('/back-office/orders') }, duration: 12000 });
        const fresh = newOrders(data.orders, saidOrders.current);
        fresh.forEach((o) => saidOrders.current.add(o.id));
        const ow = orderWords(fresh, data.orders_more);
        if (ow) {
          toast(ow, { action: { label: 'Sales', onClick: () => navigate('/back-office/sales') }, duration: 8000 });
          chime();
        }
      } catch { /* the next ask tries again */ }
    };
    ask();
    const t = setInterval(ask, EVERY);
    const back = () => { if (!document.hidden) ask(); };
    document.addEventListener('visibilitychange', back);
    return () => { stopped = true; clearInterval(t); document.removeEventListener('visibilitychange', back); };
  }, [navigate]);

  return null;
}
